import { useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRODUCT_OPTIONS, getProduct } from "@/data/products";
import { trackEvent } from "@/lib/analytics";
import { whatsappLink } from "@/config/site";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+0-9()\-\s]{9,20}$/;

const Field = ({ id, label, required, error, hint, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-semibold">
      {label}
      {required ? <span className="text-[#B42318]" aria-hidden="true"> *</span> : <span className="font-normal text-muted-foreground"> (optional)</span>}
    </Label>
    {children}
    {hint && !error ? (
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {hint}
      </p>
    ) : null}
    {error ? (
      <p id={`${id}-error`} className="text-xs font-medium text-[#B42318]" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);

const inputCls = "h-11 rounded-xl bg-white text-[#25332B] placeholder:text-[#7b8780] border-[rgba(40,91,69,0.22)] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]";

export const EnquiryForm = ({ defaultProduct = "general", compact = false, requireSurname = false, idPrefix = "enquiry" }) => {
  const location = useLocation();
  const initial = useMemo(
    () => ({
      first_name: "",
      surname: "",
      email: "",
      phone: "",
      product: defaultProduct,
      message: "",
      preferred_contact: "whatsapp",
      privacy_ack: false,
      website: "",
    }),
    [defaultProduct]
  );
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [draftUrl, setDraftUrl] = useState("");

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDraftUrl("");
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name.";
    if (requireSurname && !form.surname.trim()) e.surname = "Please enter your surname.";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) e.phone = "Please enter a valid phone number, e.g. 065 377 8765.";
    if ((form.preferred_contact === "whatsapp" || form.preferred_contact === "phone") && !form.phone.trim())
      e.phone = "Please add a phone / WhatsApp number so we can reach you.";
    if (form.message.trim().length < 5) e.message = "Please tell us a little more about your enquiry.";
    if (!form.privacy_ack) e.privacy_ack = "Please acknowledge the privacy notice to send your enquiry.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) {
      const first = document.querySelector(`[data-form="${idPrefix}"] [aria-invalid="true"]`);
      first?.focus();
      return;
    }
    if (form.website) return;
    const product = getProduct(form.product);
    const preferredContact = { whatsapp: "WhatsApp", email: "Email", phone: "Phone call" }[form.preferred_contact];
    const message = [
      "Hello Uhlumo Organics. I would like to make an enquiry.",
      "",
      `Name: ${[form.first_name.trim(), form.surname.trim()].filter(Boolean).join(" ")}`,
      `Email: ${form.email.trim()}`,
      `Phone / WhatsApp: ${form.phone.trim() || "Not supplied"}`,
      `Product: ${product?.name || "General enquiry"}`,
      `Preferred reply method: ${preferredContact}`,
      "",
      "Message:",
      form.message.trim(),
      "",
      "I agree that Uhlumo Organics may use my details to respond to this enquiry. This is not a marketing signup.",
      `Enquiry page: ${location.pathname}`,
    ].join("\n");
    const url = whatsappLink(message);
    setDraftUrl(url);
    window.open(url, "_blank", "noopener,noreferrer");
    trackEvent("enquiry_whatsapp_open", { product: form.product });
  };
  const aria = (k) => ({ "aria-invalid": errors[k] ? "true" : "false", "aria-describedby": errors[k] ? `${idPrefix}-${k}-error` : undefined });
  const id = (k) => `${idPrefix}-${k}`;

  return (
    <form noValidate onSubmit={onSubmit} className="space-y-5" data-form={idPrefix} data-testid={`${idPrefix}-form`}>
      <p className="text-sm text-muted-foreground">Complete your details to prepare a WhatsApp message. Review it and tap Send in WhatsApp to send your enquiry.</p>
      {/* Honeypot — hidden from people, attractive to bots */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor={id("website")}>Website</label>
        <input id={id("website")} name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field id={id("first_name")} label="First name" required error={errors.first_name}>
          <Input id={id("first_name")} autoComplete="given-name" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} maxLength={60} {...aria("first_name")} data-testid={`${idPrefix}-first-name-input`} />
        </Field>
        <Field id={id("surname")} label="Surname" required={requireSurname} error={errors.surname}>
          <Input id={id("surname")} autoComplete="family-name" className={inputCls} value={form.surname} onChange={(e) => set("surname", e.target.value)} maxLength={60} {...aria("surname")} data-testid={`${idPrefix}-surname-input`} />
        </Field>
        <Field id={id("email")} label="Email address" required error={errors.email}>
          <Input id={id("email")} type="email" inputMode="email" autoComplete="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} maxLength={120} {...aria("email")} data-testid={`${idPrefix}-email-input`} />
        </Field>
        <Field id={id("phone")} label="Phone / WhatsApp" required={form.preferred_contact !== "email"} error={errors.phone}>
          <Input id={id("phone")} type="tel" inputMode="tel" autoComplete="tel" placeholder="e.g. 065 377 8765" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} maxLength={20} {...aria("phone")} data-testid={`${idPrefix}-phone-input`} />
        </Field>
      </div>

      <Field id={id("product")} label="Product required" required>
        <Select value={form.product} onValueChange={(v) => set("product", v)}>
          <SelectTrigger id={id("product")} className={inputCls} data-testid={`${idPrefix}-product-select`}>
            <SelectValue placeholder="Choose a product" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {PRODUCT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`${idPrefix}-product-option-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field id={id("message")} label="Message" required error={errors.message} hint="For example: sizes, ingredients or label details, pricing, delivery area or ordering.">
        <Textarea id={id("message")} rows={compact ? 3 : 5} className="rounded-xl bg-white border-[rgba(40,91,69,0.22)] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" value={form.message} onChange={(e) => set("message", e.target.value)} maxLength={2000} {...aria("message")} data-testid={`${idPrefix}-message-input`} />
      </Field>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold">Preferred contact method</legend>
        <p className="text-xs text-muted-foreground">How you would like us to reply. Your enquiry will open in WhatsApp.</p>
        <RadioGroup value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)} className="flex flex-wrap gap-2">
          {[
            ["whatsapp", "WhatsApp"],
            ["email", "Email"],
            ["phone", "Phone call"],
          ].map(([v, l]) => (
            <Label
              key={v}
              htmlFor={id(`pc-${v}`)}
              className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border px-4 text-sm font-medium transition-colors ${form.preferred_contact === v ? "border-[color:var(--u-green)] bg-[#EEF3EC]" : "bg-white border-[rgba(40,91,69,0.18)]"}`}
            >
              <RadioGroupItem id={id(`pc-${v}`)} value={v} data-testid={`${idPrefix}-preferred-contact-${v}-radio`} />
              {l}
            </Label>
          ))}
        </RadioGroup>
      </fieldset>

      <div className="space-y-1.5">
        <div className="flex items-start gap-3 rounded-xl border bg-[#FAF9F5] p-4" style={{ borderColor: errors.privacy_ack ? "#B42318" : "rgba(40,91,69,0.16)" }}>
          <Checkbox id={id("privacy")} checked={form.privacy_ack} onCheckedChange={(v) => set("privacy_ack", v === true)} className="mt-0.5" aria-invalid={errors.privacy_ack ? "true" : "false"} aria-describedby={errors.privacy_ack ? `${idPrefix}-privacy_ack-error` : undefined} data-testid={`${idPrefix}-privacy-checkbox`} />
          <Label htmlFor={id("privacy")} className="text-sm font-normal leading-relaxed text-[#3d4a42]">
            I agree that Uhlumo Organics may use my details to respond to this enquiry, as described in the{" "}
            <Link to="/privacy-policy" className="link-accent" target="_blank">
              privacy notice
            </Link>
            . This does not sign me up for marketing.
          </Label>
        </div>
        {errors.privacy_ack ? (
          <p id={`${idPrefix}-privacy_ack-error`} className="text-xs font-medium text-[#B42318]" role="alert">
            {errors.privacy_ack}
          </p>
        ) : null}
      </div>

      {errors._form ? (
        <p className="rounded-xl bg-[#FDECEA] p-3 text-sm text-[#7A1C12]" role="alert" data-testid={`${idPrefix}-form-error`}>
          {errors._form}
        </p>
      ) : null}

      {draftUrl ? (
        <div className="rounded-xl border bg-[#F1F6F0] p-4 text-sm" role="status" data-testid={`${idPrefix}-whatsapp-ready`}>
          <p>Your message is ready. It has not been sent yet. Tap Send in WhatsApp to complete your enquiry.</p>
          <a className="link-accent mt-2 inline-block" href={draftUrl} target="_blank" rel="noopener noreferrer">Open your WhatsApp message</a>
        </div>
      ) : null}

      <button type="submit" className="btn btn-primary w-full sm:w-auto sm:px-8" data-testid={`${idPrefix}-submit-button`}>
        <Send className="h-4 w-4" aria-hidden="true" />
        Continue to WhatsApp
      </button>
    </form>
  );
};
